<template>
    <div class="custom-block-config">
        <div v-if="loading" class="loading-state">
            <a-spin />
        </div>

        <!-- Show form directly if block already selected -->
        <div v-else-if="blockDefinition" class="config-form">
            <!-- Block Info Header -->
            <div class="block-info-header">
                <span class="material-icons">{{ blockDefinition.icon || "extension" }}</span>
                <div class="info">
                    <h4>{{ blockDefinition.name }}</h4>
                    <p>{{ blockDefinition.description }}</p>
                </div>
            </div>

            <a-divider />

            <!-- Dynamic Form Fields -->
            <div class="form-fields">
                <div v-for="param in visibleParameters" :key="param.name" class="form-field">
                    <!-- Text -->
                    <template v-if="param.type === 'text'">
                        <div class="field-label">
                            {{ param.label }}
                            <span v-if="param.required" class="required">*</span>
                        </div>
                        <div class="mode-switch">
                            <a-radio-group v-model="paramModes[param.name]" button-style="solid" size="small">
                                <a-radio-button value="static">{{ $t("auto.chatbotStatic") }}</a-radio-button>
                                <a-radio-button value="dynamic">{{ $t("auto.chatbotDynamic") }}</a-radio-button>
                            </a-radio-group>
                        </div>
                        <div v-if="paramModes[param.name] === 'static'">
                            <a-input v-model="formValues[param.name]" :placeholder="param.description" />
                        </div>
                        <div v-else>
                            <TiptapEditor
                                v-model="formValues[param.name]"
                                :placeholder="param.description || param.label"
                                :rows="2"
                            />
                        </div>
                    </template>

                    <!-- Number -->
                    <template v-else-if="param.type === 'number'">
                        <div class="field-label">
                            {{ param.label }}
                            <span v-if="param.required" class="required">*</span>
                        </div>
                        <div class="mode-switch">
                            <a-radio-group v-model="paramModes[param.name]" button-style="solid" size="small">
                                <a-radio-button value="static">{{ $t("auto.chatbotStatic") }}</a-radio-button>
                                <a-radio-button value="dynamic">{{ $t("auto.chatbotDynamic") }}</a-radio-button>
                            </a-radio-group>
                        </div>
                        <div v-if="paramModes[param.name] === 'static'">
                            <a-input-number
                                v-model="formValues[param.name]"
                                style="width: 100%"
                                :placeholder="param.description"
                            />
                        </div>
                        <div v-else>
                            <TiptapEditor
                                v-model="formValues[param.name]"
                                :placeholder="param.description || param.label"
                                :rows="1"
                            />
                        </div>
                    </template>

                    <!-- Boolean -->
                    <template v-else-if="param.type === 'boolean'">
                        <div class="field-label">
                            {{ param.label }}
                            <span v-if="param.required" class="required">*</span>
                        </div>
                        <div class="mode-switch">
                            <a-radio-group v-model="paramModes[param.name]" button-style="solid" size="small">
                                <a-radio-button value="static">{{ $t("auto.chatbotStatic") }}</a-radio-button>
                                <a-radio-button value="dynamic">{{ $t("auto.chatbotDynamic") }}</a-radio-button>
                            </a-radio-group>
                        </div>
                        <div v-if="paramModes[param.name] === 'static'">
                            <a-checkbox v-model="formValues[param.name]">
                                {{ param.description || "Etkin" }}
                            </a-checkbox>
                        </div>
                        <div v-else>
                            <TiptapEditor
                                v-model="formValues[param.name]"
                                :placeholder="param.description || 'true/false or {{attribute}}'"
                                :rows="1"
                            />
                        </div>
                    </template>

                    <!-- User ID -->
                    <template v-else-if="param.type === 'userid'">
                        <div class="field-label">
                            {{ param.label }}
                            <span v-if="param.required" class="required">*</span>
                        </div>
                        <div class="mode-switch">
                            <a-radio-group v-model="paramModes[param.name]" button-style="solid" size="small">
                                <a-radio-button value="static">{{ $t("auto.chatbotStatic") }}</a-radio-button>
                                <a-radio-button value="dynamic">{{ $t("auto.chatbotDynamic") }}</a-radio-button>
                            </a-radio-group>
                        </div>
                        <div v-if="paramModes[param.name] === 'static'">
                            <a-select
                                v-model="formValues[param.name]"
                                class="classic-select"
                                :placeholder="param.description || 'Select user'"
                                allowClear
                            >
                                <a-select-option v-for="user in users" :key="user._id" :value="user._id">
                                    {{ user.name }}
                                </a-select-option>
                            </a-select>
                        </div>
                        <div v-else>
                            <TiptapEditor
                                v-model="formValues[param.name]"
                                :placeholder="param.description || param.label"
                                :rows="1"
                            />
                        </div>
                    </template>

                    <!-- User ID List -->
                    <template v-else-if="param.type === 'userid_list'">
                        <div class="field-label">
                            {{ param.label }}
                            <span v-if="param.required" class="required">*</span>
                        </div>
                        <div class="mode-switch">
                            <a-radio-group v-model="paramModes[param.name]" button-style="solid" size="small">
                                <a-radio-button value="static">{{ $t("auto.chatbotStatic") }}</a-radio-button>
                                <a-radio-button value="dynamic">{{ $t("auto.chatbotDynamic") }}</a-radio-button>
                            </a-radio-group>
                        </div>
                        <div v-if="paramModes[param.name] === 'static'">
                            <a-select
                                v-model="formValues[param.name]"
                                mode="multiple"
                                class="classic-select"
                                :placeholder="param.description || 'Select users'"
                            >
                                <a-select-option v-for="user in users" :key="user._id" :value="user._id">
                                    {{ user.name }}
                                </a-select-option>
                            </a-select>
                        </div>
                        <div v-else>
                            <TiptapEditor
                                v-model="formValues[param.name]"
                                :placeholder="param.description || param.label"
                                :rows="1"
                            />
                        </div>
                    </template>

                    <!-- JSON Object / Raw JSON -->
                    <template v-else-if="param.type === 'json'">
                        <div class="field-label">
                            {{ param.label }}
                            <span v-if="param.required" class="required">*</span>
                        </div>
                        <div class="mode-switch">
                            <a-radio-group v-model="paramModes[param.name]" button-style="solid" size="small">
                                <a-radio-button value="static">{{ $t("auto.chatbotStatic") }}</a-radio-button>
                                <a-radio-button value="dynamic">{{ $t("auto.chatbotDynamic") }}</a-radio-button>
                            </a-radio-group>
                        </div>
                        <div v-if="paramModes[param.name] === 'static'">
                            <a-textarea
                                v-model="formValues[param.name]"
                                :rows="4"
                                :placeholder="param.description || 'Enter JSON object'"
                            />
                        </div>
                        <div v-else>
                            <TiptapEditor
                                v-model="formValues[param.name]"
                                :placeholder="param.description || param.label"
                                :rows="4"
                            />
                        </div>
                    </template>

                    <!-- Structured JSON -->
                    <template v-else-if="param.type === 'json-structured'">
                        <div class="field-label d-flex flex-column">
                            <strong>{{ param.label }} <span v-if="param.required" class="required">*</span> </strong>
                            <span v-if="param.description" class="description">{{ param.description }}</span>
                            
                        </div>
                        <div class="mode-switch">
                            <a-radio-group v-model="paramModes[param.name]" button-style="solid" size="small">
                                <a-radio-button value="static">Form</a-radio-button>
                                
                            </a-radio-group>
                        </div>

                        <!-- Static Form Mode -->
                        <div v-if="paramModes[param.name] === 'static'">
                            <StructuredJsonForm
                                v-model="formValues[param.name]"
                                :schema="param.schema || []"
                                :is-list="param.isList || false"
                            />
                        </div>

                    </template>

                    <!-- Array -->
                    <template v-else-if="param.type === 'array'">
                        <div class="field-label">
                            {{ param.label }}
                            <span v-if="param.required" class="required">*</span>
                        </div>
                        <div class="mode-switch">
                            <a-radio-group v-model="paramModes[param.name]" button-style="solid" size="small">
                                <a-radio-button value="static">{{ $t("auto.chatbotStatic") }}</a-radio-button>
                                <a-radio-button value="dynamic">{{ $t("auto.chatbotDynamic") }}</a-radio-button>
                            </a-radio-group>
                        </div>
                        <div v-if="paramModes[param.name] === 'static'">
                            <a-select
                                v-model="formValues[param.name]"
                                mode="tags"
                                :placeholder="param.description || 'Enter array items'"
                            />
                        </div>
                        <div v-else>
                            <TiptapEditor
                                v-model="formValues[param.name]"
                                :placeholder="param.description || param.label"
                                :rows="2"
                            />
                        </div>
                    </template>
                </div>
            </div>

            <!-- Output Preview (if available) -->
            <div v-if="outputSchema" class="output-preview">
                <h4>{{ $t("chatbotv2.outputPreview") || "Output Preview" }}</h4>
                <pre>{{ JSON.stringify(outputSchema, null, 2) }}</pre>
            </div>
        </div>

        <!-- Block Selector (only show if no block selected yet) -->
        <div v-else class="block-selector">
            <h3>{{ $t("chatbotv2.selectCustomBlock") || "Select Custom Block" }}</h3>
            <p>{{ $t("chatbotv2.selectCustomBlockDesc") || "Choose a custom block from your imported blocks" }}</p>

            <div class="blocks-list">
                <div
                    class="block-option"
                    v-for="importedBlock in importedBlocks"
                    :key="importedBlock.blockId"
                    @click="selectBlock(importedBlock)"
                >
                    <span class="material-icons">{{ $_.get(importedBlock, "block.icon", "extension") }}</span>
                    <div class="block-info">
                        <strong>{{
                            importedBlock.customName || $_.get(importedBlock, "block.name", "Bilinmeyen Blok")
                        }}</strong>
                        <small>{{ $_.get(importedBlock, "block.description", "Bilinmeyen Blok") }}</small>
                    </div>
                </div>
            </div>

            <div class="no-blocks" v-if="importedBlocks.length === 0">
                <a-empty :description="$t('chatbotv2.noImportedBlocks') || 'No imported blocks'">
                    <a-button type="primary" @click="openBlockMarket">
                        {{ $t("chatbotv2.browseBlockMarket") || "Browse Block Market" }}
                    </a-button>
                </a-empty>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import TiptapEditor from "../common/TiptapEditor.vue";
import StructuredJsonForm from "./StructuredJsonForm.vue";
import { CUSTOM_BLOCKS_API } from "../../../../../../api/chatbotCustomBlocks";
import _ from "lodash";

export default {
    name: "CustomBlockConfig",
    components: {
        TiptapEditor,
        StructuredJsonForm,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        blockId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            blockDefinition: null,
            formValues: {},
            paramModes: {}, // Track static/dynamic mode for each parameter
            outputSchema: null,
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users?.data || [],
            importedBlocks: (state) => state.customBlocks.importedBlocks,
        }),
        visibleParameters() {
            if (!this.blockDefinition || !this.blockDefinition.parameters) return [];
            return this.blockDefinition.parameters.filter((p) => !p.hidden);
        },
        allParameters() {
            if (!this.blockDefinition || !this.blockDefinition.parameters) return [];
            return this.blockDefinition.parameters;
        },
    },
    methods: {
        async loadBlockDefinition() {
            this.loading = true;
            try {
                const customBlockId = this.value.customBlockId;
                if (!customBlockId) {
                    this.loading = false;
                    return;
                }

                // Get block details
                const { data } = await CUSTOM_BLOCKS_API.getDetails(customBlockId, false);
                this.blockDefinition = data;

                // Initialize form values and modes
                this.initializeFormValues();

                // Wait for Vue to update reactive data
                await this.$nextTick();

                // Then initialize parameter modes based on the values
                this.initializeParamModes();

                this.outputSchema = this.value.outputSchema;
            } catch (error) {
                console.error("Failed to load block definition:", error);
                this.$message.error(error.message || "Failed to load block definition");
            } finally {
                this.loading = false;
            }
        },
        initializeFormValues() {
            const values = {};

            this.allParameters.forEach((param) => {
                // Check if value already exists in payload
                if (this.value.parameters && this.value.parameters[param.name] !== undefined) {
                    values[param.name] = this.value.parameters[param.name];
                } else {
                    // Use default value
                    let defaultVal = param.defaultValue;

                    // Auto-populate special types
                    if (param.type === "chatid" && !defaultVal) {
                        defaultVal = "{{conversationId}}";
                    } else if (param.type === "contactid" && !defaultVal) {
                        defaultVal = "{{id}}";
                    } else if (param.type === "json-structured" && !defaultVal) {
                        // Initialize based on schema
                        if (param.isList) {
                            defaultVal = [];
                        } else {
                            // Create default object from schema
                            const obj = {};
                            if (param.schema && Array.isArray(param.schema)) {
                                param.schema.forEach((field) => {
                                    if (field.defaultValue !== undefined && field.defaultValue !== null) {
                                        obj[field.name] = field.defaultValue;
                                    } else if (field.type === "boolean") {
                                        obj[field.name] = false;
                                    } else if (field.type === "array") {
                                        obj[field.name] = [];
                                    } else if (field.type === "number") {
                                        obj[field.name] = 0;
                                    } else {
                                        obj[field.name] = "";
                                    }
                                });
                            }
                            defaultVal = obj;
                        }
                    }

                    values[param.name] = defaultVal;
                }
            });

            this.formValues = values;
        },
        initializeParamModes() {
            const modes = {};

            this.allParameters.forEach((param) => {
                // First check if inputMode is explicitly set in parameter definition
                if (param.inputMode) {
                    modes[param.name] = param.inputMode;
                    return;
                }

                // Get value from formValues or directly from value.parameters
                let value = this.formValues[param.name];
                if (value === undefined && this.value.parameters) {
                    value = this.value.parameters[param.name];
                }

                // Check if value contains attributes ({{...}})
                let hasDynamicValue = false;

                if (typeof value === "string" && value !== null && value !== undefined) {
                    // Check for {{...}} pattern
                    hasDynamicValue = value.includes("{{") && value.includes("}}");
                } else if (Array.isArray(value)) {
                    // Check if any array item contains {{...}}
                    hasDynamicValue = value.some(
                        (item) => typeof item === "string" && item.includes("{{") && item.includes("}}")
                    );
                } else if (value && typeof value === "object") {
                    // Check if JSON object contains {{...}} in stringified form
                    const jsonStr = JSON.stringify(value);
                    hasDynamicValue = jsonStr.includes("{{") && jsonStr.includes("}}");
                }

                modes[param.name] = hasDynamicValue ? "dynamic" : "static";
            });

            this.paramModes = modes;
        },
        selectBlock(importedBlock) {
            const payload = {
                customBlockId: importedBlock.blockId,
                versionId: importedBlock.versionId,
                parameters: {},
                outputSchema: null,
            };
            this.$emit("input", payload);
        },
        openBlockMarket() {
            this.$router.push("/chatbot/block-market");
        },
        emitValue() {
            const payload = {
                ...this.value,
                parameters: this.formValues,
            };
            this.$emit("input", payload);
        },
    },
    watch: {
        formValues: {
            deep: true,
            handler() {
                this.emitValue();
            },
        },
        // When mode changes, clear value if switching from dynamic to static
        paramModes: {
            deep: true,
            handler(newModes, oldModes) {
                Object.keys(newModes).forEach((paramName) => {
                    if (oldModes[paramName] === "dynamic" && newModes[paramName] === "static") {
                        const value = this.formValues[paramName];
                        if (typeof value === "string" && (value.includes("{{") || value.includes("}}"))) {
                            this.formValues[paramName] = "";
                        }
                    }
                });
            },
        },
        "value.customBlockId": {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.loadBlockDefinition();
                }
            },
        },
    },
    created() {
        // Load imported blocks
        this.$store.dispatch("customBlocks/listImportedBlocks").catch((error) => {
            console.error("Failed to load imported blocks:", error);
        });

        if (this.value.customBlockId) {
            this.loadBlockDefinition();
        } else {
            this.loading = false;
        }
    },
};
</script>

<style lang="scss" scoped>
.custom-block-config {
    padding: 20px;

    .loading-state {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 200px;
        color: #999;
    }

    .block-selector {
        padding: 20px;

        h3 {
            margin: 0 0 8px 0;
        }

        p {
            color: #666;
            margin-bottom: 20px;
        }

        .blocks-list {
            display: flex;
            flex-direction: column;
            gap: 12px;
            max-height: 400px;
            overflow-y: auto;

            .block-option {
                display: flex;
                gap: 16px;
                align-items: center;
                padding: 16px;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.3s;

                &:hover {
                    border-color: #7c4dff;
                    background: #f5f5f5;
                    transform: translateX(4px);
                }

                .material-icons {
                    font-size: 40px;
                    color: #7c4dff;
                }

                .block-info {
                    flex: 1;

                    strong {
                        display: block;
                        margin-bottom: 4px;
                        font-size: 16px;
                    }

                    small {
                        color: #666;
                        font-size: 13px;
                    }
                }
            }
        }

        .no-blocks {
            padding: 40px 0;
        }
    }

    .block-info-header {
        display: flex;
        gap: 16px;
        align-items: flex-start;
        margin-bottom: 20px;

        .material-icons {
            font-size: 48px;
            color: #7c4dff;
        }

        .info {
            flex: 1;

            h4 {
                margin: 0 0 8px 0;
                font-size: 18px;
                font-weight: 600;
            }

            p {
                margin: 0;
                color: #666;
                font-size: 14px;
            }
        }
    }

    .config-form {
        .form-fields {
            display: flex;
            flex-direction: column;
            gap: 20px;

            .form-field {
                .field-label {
                    margin-bottom: 8px;
                    font-weight: 500;

                    .required {
                        color: red;
                        margin-left: 4px;
                    }
                }

                .mode-switch {
                    margin-bottom: 10px;
                    display: flex;
                    justify-content: flex-end;
                }

                .classic-select {
                    width: 100%;
                }
            }
        }

        .output-preview {
            margin-top: 24px;
            padding: 16px;
            background: #f5f5f5;
            border-radius: 8px;

            h4 {
                margin: 0 0 12px 0;
            }

            pre {
                background: #1e1e1e;
                color: #d4d4d4;
                padding: 12px;
                border-radius: 4px;
                font-size: 12px;
                overflow-x: auto;
                margin: 0;
            }
        }
    }
}
</style>
